/**
 * ProseAnswer - typed renderer for the `prose` payload (`qa`, `overview`).
 *
 * Lead answer paragraph(s), then the optional key points. Inline `[N]`
 * markers resolve against the envelope's citation list.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProsePayload, ResearchCitation } from '../types';
import { Well } from '../../shared/brand/Field';
import { CitationChips, CitationHandlers, citationNumbers } from './CitationChip';
import InlineCitedText from './InlineCitedText';

interface Props extends CitationHandlers {
  payload: ProsePayload;
  citations: ResearchCitation[];
}

/** The lead paragraph, under either spelling the engine has used. */
const leadAnswer = (payload: ProsePayload): string => payload.answer || payload.prose || '';

const ProseAnswer: React.FC<Props> = ({ payload, citations, ...handlers }) => {
  const { t } = useTranslation();
  const answer = leadAnswer(payload);
  const keyPoints = Array.isArray(payload.key_points) ? payload.key_points : [];
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const caveats = Array.isArray(payload.caveats) ? payload.caveats : [];

  return (
    <div className="font-sans space-y-4 animate-fade-in" data-testid="prose-answer">
      {payload.title && (
        <h3 className="text-sm font-medium text-fg-strong">{payload.title}</h3>
      )}

      {answer.split(/\n{2,}/).filter((p) => p.trim()).map((para, i) => (
        // Staggered entrance - the answer arrives whole, this just lands it.
        <p
          key={i}
          className="text-sm leading-relaxed text-fg-body reveal-item"
          style={{ '--reveal-delay': `${Math.min(i, 8) * 60}ms` } as React.CSSProperties}
        >
          <InlineCitedText text={para} citations={citations} {...handlers} />
        </p>
      ))}

      {keyPoints.length > 0 && (
        <ul className="space-y-1.5" data-testid="prose-key-points">
          {keyPoints.map((point, i) => {
            const text = typeof point === 'string' ? point : point.text || point.point || '';
            const refs = typeof point === 'string' ? [] : citationNumbers(point);
            if (!text) return null;
            return (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-fg-body reveal-item"
                style={{ '--reveal-delay': `${Math.min(i, 8) * 60}ms` } as React.CSSProperties}
              >
                <span
                  className="mt-[7px] w-1.5 h-1.5 rounded-full bg-champagne flex-shrink-0"
                  aria-hidden
                />
                <span className="leading-relaxed">
                  <InlineCitedText text={text} citations={citations} {...handlers} />
                  <CitationChips numbers={refs} citations={citations} {...handlers} />
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {sections.map((section, i) => (
        <Well key={i} className="space-y-1">
          {section.heading && (
            <h4 className="text-[13px] font-medium text-fg-strong">{section.heading}</h4>
          )}
          {section.body && (
            <p className="text-sm leading-relaxed text-fg-body">
              <InlineCitedText text={section.body} citations={citations} {...handlers} />
              <CitationChips
                numbers={citationNumbers(section)}
                citations={citations}
                {...handlers}
              />
            </p>
          )}
        </Well>
      ))}

      {caveats.length > 0 && (
        <div className="pt-1 border-t border-hairline-soft">
          <p className="text-[11px] font-medium text-fg-muted mb-1">{t('Caveats')}</p>
          <ul className="space-y-1">
            {caveats.map((caveat, i) => (
              <li key={i} className="text-xs text-fg-muted leading-relaxed">
                {caveat}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProseAnswer;
